/**
 * Picks what an assessment cites, one source at a time (investigations, documents, alerts,
 * research runs): a searchable list with the picked items checked. The feature supplies each
 * source's items.
 */
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { EvidenceKind, EvidenceRefIn } from '@/types/analytic-hypothesis'
import { cn } from '@/utils/cn'

const COPY = 'analyticHypotheses.form'

/** An item that can be cited, as its picker lists it. */
export interface EvidenceOption {
  id: string
  label: string
  /** A second line: a date, a type. */
  hint?: string
}

/** One source of citable items; ``items`` null while it is still being read. */
export interface EvidenceSource {
  kind: EvidenceKind
  items: readonly EvidenceOption[] | null
}

export interface EvidencePickerProps {
  source: EvidenceSource
  picked: readonly EvidenceRefIn[]
  onToggle: (ref: EvidenceRefIn) => void
}

export function EvidencePicker({ source, picked, onToggle }: EvidencePickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const chosen = new Set(picked.filter(ref => ref.kind === source.kind).map(ref => ref.id))
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="xs" aria-expanded={open}>
          {t(`${COPY}.pick.${source.kind}`)}
          {chosen.size > 0 && <span className="font-mono tabular-nums">{chosen.size}</span>}
          <ChevronsUpDown aria-hidden className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] p-0 md:w-80" align="start">
        <Command>
          <CommandInput placeholder={t(`${COPY}.search`)} className="h-9" />
          <CommandList className="max-h-64">
            <CommandEmpty>
              {source.items === null ? t(`${COPY}.loadingOptions`) : t(`${COPY}.noOptions`)}
            </CommandEmpty>
            <CommandGroup>
              {(source.items ?? []).map(item => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  keywords={[item.label, item.hint ?? '']}
                  onSelect={() => onToggle({ kind: source.kind, id: item.id })}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{item.label}</span>
                    {item.hint && (
                      <span className="truncate text-2xs text-muted-foreground">{item.hint}</span>
                    )}
                  </div>
                  <Check
                    aria-hidden
                    className={cn(
                      'size-3.5 shrink-0',
                      chosen.has(item.id) ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
